import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ArrowRight, Monitor, Smartphone, X } from 'lucide-react';

/**
 * Popup shown when the user clicks "Generate POM". Asks whether the automation
 * target is Web or Mobile (Appium/Android), then routes into the matching
 * workflow. For Mobile, the run target (local emulator vs BrowserStack cloud
 * device) is chosen later, at the automation step.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (type: 'web' | 'mobile') => void
 */
const TestingTypeModal = ({ open, onClose, onSelect }) => {
    useEffect(() => {
        if (!open) return undefined;
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [open, onClose]);

    if (!open) return null;

    const options = [
        {
            type: 'web',
            title: 'Web testing',
            description: 'Generate Playwright page objects and scripts for a web application.',
            icon: Monitor,
            accent: 'from-blue-600 to-cyan-600',
            iconWrap: 'border-blue-200 bg-blue-50 text-blue-700',
        },
        {
            type: 'mobile',
            title: 'Mobile testing',
            description: 'Generate Appium (Android) page objects and scripts. Choose where to run — local emulator (Android Studio) or a BrowserStack cloud device — at the automation step.',
            icon: Smartphone,
            accent: 'from-emerald-600 to-green-600',
            iconWrap: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        },
    ];

    return ReactDOM.createPortal(
        <div className="modal-overlay" role="presentation" onClick={onClose}>
            <div
                className="modal-shell max-w-3xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="testing-type-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="border-b border-indigo-200 bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-4 text-white">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h4 id="testing-type-title" className="text-lg font-semibold">Choose automation type</h4>
                            <p className="mt-1 text-sm text-indigo-50">
                                Select the platform you want to generate Page Object Models for.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/40 bg-white/10 text-white hover:bg-white/20"
                            aria-label="Close automation type dialog"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div className="grid gap-3 bg-white px-5 py-5 sm:grid-cols-2">
                    {options.map(({ type, title, description, icon: Icon, accent, iconWrap }) => (
                        <button
                            key={type}
                            type="button"
                            onClick={() => onSelect?.(type)}
                            className="group flex h-full flex-col items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:shadow-[0_18px_40px_-28px_rgba(15,23,42,0.9)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border ${iconWrap}`}>
                                <Icon className="h-5 w-5" />
                            </span>
                            <div className="min-w-0">
                                <p className="text-base font-semibold text-slate-900">{title}</p>
                                <p className="mt-1 text-sm text-slate-600">{description}</p>
                            </div>
                            <span className={`mt-auto inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r ${accent} px-3 py-1.5 text-sm font-semibold text-white`}>
                                Continue
                                <ArrowRight className="h-4 w-4" />
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TestingTypeModal;
